// Approval workflow API client + types

export type ApprovalActionType = 'deploy-agent' | 'modify-policy' | 'export-dlp';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface ApprovalRequest {
  approvalId: string;
  actionType: ApprovalActionType;
  actionLabel: string;
  resourceId?: string | null;
  resourceLabel?: string | null;
  reason?: string | null;
  metadata?: Record<string, any> | null;
  status: ApprovalStatus;
  requestedBy: string;
  requestedByName?: string;
  requestedAt: string;
  decidedBy?: string | null;
  decidedByName?: string | null;
  decidedAt?: string | null;
  decisionNote?: string | null;
}

export interface CreateApprovalPayload {
  actionType: ApprovalActionType;
  resourceId?: string;
  resourceLabel?: string;
  reason?: string;
  metadata?: Record<string, any>;
}

/**
 * Create a new approval request for a sensitive operation.
 */
export async function createApproval(
  apiUrl: string,
  payload: CreateApprovalPayload
): Promise<{ approvalId: string; status: ApprovalStatus }> {
  const res = await fetch(`${apiUrl}/api/admin/approvals/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Failed to create approval (${res.status})`);
  }
  return res.json();
}

/**
 * List approval requests. Optionally filter by status or "mine".
 */
export async function listApprovals(
  apiUrl: string,
  opts?: { status?: ApprovalStatus; mine?: boolean }
): Promise<ApprovalRequest[]> {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  if (opts?.mine) params.set('mine', 'true');
  const qs = params.toString();
  const res = await fetch(`${apiUrl}/api/admin/approvals${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error(`Failed to list approvals (${res.status})`);
  const data = await res.json();
  return data.approvals || [];
}

/**
 * Fetch a single approval (used for polling status after a request).
 */
export async function getApproval(apiUrl: string, approvalId: string): Promise<ApprovalRequest> {
  const res = await fetch(`${apiUrl}/api/admin/approvals/${approvalId}`);
  if (!res.ok) throw new Error(`Failed to fetch approval (${res.status})`);
  return res.json();
}

/**
 * Approve or reject a request (Admin / Security Officer only).
 */
export async function decideApproval(
  apiUrl: string,
  approvalId: string,
  decision: 'approved' | 'rejected',
  note?: string
): Promise<{ approvalId: string; status: ApprovalStatus; decidedBy: string }> {
  const res = await fetch(`${apiUrl}/api/admin/approvals/${approvalId}/decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision, note }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Failed to submit decision (${res.status})`);
  }
  return res.json();
}
